import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';

import { SignupPage } from '@/pages/Signup';
import { LoginPage } from '@/pages/Login';
import { CalendarsListPage } from '@/pages/CalendarsList';
import { EventsCalendarPage } from '@/pages/EventsCalendar';
import { VoicePage } from '@/pages/Voice';
import { SetupDevicePage } from '@/pages/SetupDevice';
import { WelcomePage } from '@/pages/Welcome';
import { OrderPage } from '@/pages/Order';
import { ByoSetupPage } from '@/pages/ByoSetup';
import { AcceptInvitePage } from '@/pages/AcceptInvite';
import { SettingsPage } from '@/pages/Settings';
import { ActivityPage } from '@/pages/Activity';
import { NotFoundPage } from '@/pages/NotFound';

import { AppShell } from '@/components/AppShell';
import { RequireAuth } from '@/components/RequireAuth';
import { EnsureUserProfile } from '@/components/EnsureUserProfile';
import { RootRedirect } from '@/components/RootRedirect';
import { RouteTracker } from '@/components/RouteTracker';

/** Old per-section URLs (/profile, /devices, /members) all redirect
 * into the combined Settings page. Old bookmarks / EldersDropdown
 * navigations / inbound links keep working. */
function RedirectToSettings() {
  const { aid } = useParams<{ aid: string }>();
  return <Navigate to={`/accounts/${aid}/settings`} replace />;
}

export const router = createBrowserRouter([
  {
    // Root layout: RouteTracker fires a GA4 page_view on every navigation, then
    // renders the matched route. Wraps everything so signed-out pages count too.
    element: <RouteTracker />,
    children: [
      // Auth + claim + invite — outside the protected layout because they
      // need to be reachable while signed-out.
      { path: '/signup', element: <SignupPage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/add-button', element: <SetupDevicePage /> },
      { path: '/invites/:token', element: <AcceptInvitePage /> },

      // Legacy redirects so old bookmarks / emails don't break.
      { path: '/dashboard', element: <Navigate to="/" replace /> },
      { path: '/complete-signup', element: <Navigate to="/" replace /> },
      // /order used to be account-agnostic; now scoped to /accounts/:aid/order.
      // Bounce the bare path through RootRedirect so the user lands on
      // their account's order page (or NoAccountsLanding if they have none).
      { path: '/order', element: <Navigate to="/" replace /> },

      // Authenticated app.
      {
        element: <RequireAuth />,
        children: [
          {
            element: <EnsureUserProfile />,
            children: [
              { path: '/', element: <RootRedirect /> },
              { path: '/welcome', element: <WelcomePage /> },
              {
                element: <AppShell />,
                children: [
                  { path: '/accounts/:aid/order', element: <OrderPage /> },
                  { path: '/accounts/:aid/byo', element: <ByoSetupPage /> },
                  {
                    path: '/accounts/:aid/calendars/:cid/events',
                    element: <EventsCalendarPage />,
                  },
                  { path: '/accounts/:aid/voice', element: <VoicePage /> },
                  { path: '/accounts/:aid/activity', element: <ActivityPage /> },
                  { path: '/accounts/:aid/settings', element: <SettingsPage /> },
                  // Power-user / admin escape hatch — manage calendars list.
                  { path: '/accounts/:aid/calendars', element: <CalendarsListPage /> },
                  // Old per-section URLs — fold into Settings.
                  { path: '/accounts/:aid/profile', element: <RedirectToSettings /> },
                  { path: '/accounts/:aid/devices', element: <RedirectToSettings /> },
                  { path: '/accounts/:aid/members', element: <RedirectToSettings /> },
                ],
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
